// Schemas describing each editable section. The SectionEditor renders fields based on these.
// Field types: text, textarea, image, toggle, list

import { ICON_NAMES } from "@/lib/icons";

const ICON_OPTIONS = ICON_NAMES;

export const SECTION_SCHEMAS = {
  site: {
    label: "Site & Brand",
    description: "Brand-level settings that show across the page (logo text, phone, email, social links).",
    pathRoot: "site", // edits content.site instead of sections.site
    hasToggle: false,
    fields: [
      { key: "name", label: "Brand name", type: "text" },
      { key: "tagline", label: "Tagline", type: "textarea" },
      { key: "phone", label: "Phone (display)", type: "text" },
      { key: "phone_href", label: "Phone link (tel:…)", type: "text" },
      { key: "email", label: "Email (display)", type: "text" },
      { key: "email_href", label: "Email link (mailto:…)", type: "text" },
      { key: "social.facebook", label: "Facebook URL", type: "text" },
      { key: "social.instagram", label: "Instagram URL", type: "text" },
      { key: "social.tiktok", label: "TikTok URL", type: "text" },
    ],
  },
  hero: {
    label: "Hero",
    description: "The first thing visitors see. Background image, headline, CTAs, trust badges.",
    fields: [
      { key: "eyebrow", label: "Small eyebrow text", type: "text" },
      { key: "headline_1", label: "Headline line 1", type: "text" },
      { key: "headline_2", label: "Headline line 2", type: "text" },
      { key: "headline_accent", label: "Headline accent (gold)", type: "text" },
      { key: "subheadline", label: "Subheadline", type: "textarea" },
      { key: "primary_cta", label: "Primary button label", type: "text" },
      { key: "secondary_cta", label: "Secondary button label", type: "text" },
      { key: "image", label: "Background image URL", type: "image" },
    ],
    list: {
      key: "badges",
      label: "Trust Badges",
      defaultItem: () => ({ icon: "ShieldCheck", label: "New badge" }),
      itemLabel: (b) => b.label,
      fields: [
        { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
        { key: "label", label: "Label", type: "text" },
      ],
    },
  },
  services: {
    label: "Services",
    description: "Five service cards (or as many as you like).",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Section title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
    ],
    list: {
      key: "items",
      label: "Service Cards",
      defaultItem: () => ({ id: `svc-${Date.now()}`, icon: "Truck", image: "", title: "New service", desc: "", enabled: true }),
      itemLabel: (s) => s.title,
      hasItemToggle: true,
      fields: [
        { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
        { key: "image", label: "Card image (URL)", type: "image" },
        { key: "title", label: "Title", type: "text" },
        { key: "desc", label: "Description", type: "textarea" },
      ],
    },
  },
  about: {
    label: "About",
    description: "Team image + features list.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title_1", label: "Title (white)", type: "text" },
      { key: "title_accent", label: "Title accent (gold)", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image", label: "Image URL", type: "image" },
      { key: "stat_value", label: "Stat value", type: "text" },
      { key: "stat_label", label: "Stat label", type: "text" },
    ],
    list: {
      key: "features",
      label: "Feature Badges",
      defaultItem: () => ({ id: `ft-${Date.now()}`, label: "New feature", enabled: true }),
      itemLabel: (f) => f.label,
      hasItemToggle: true,
      fields: [
        { key: "label", label: "Label", type: "text" },
      ],
    },
  },
  portfolio: {
    label: "Portfolio",
    description: "Masonry gallery of past work.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "categories", label: "Filter categories (comma separated)", type: "csv" },
    ],
    list: {
      key: "items",
      label: "Portfolio Items",
      defaultItem: () => ({ id: `p-${Date.now()}`, src: "", cat: "Moving", title: "New work", h: "mid", enabled: true }),
      itemLabel: (it) => it.title,
      hasItemToggle: true,
      fields: [
        { key: "src", label: "Image URL", type: "image" },
        { key: "title", label: "Title", type: "text" },
        { key: "cat", label: "Category", type: "text" },
        { key: "h", label: "Height", type: "select", options: ["short", "mid", "tall"] },
      ],
    },
  },
  process: {
    label: "How It Works",
    description: "Three-step process.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Title", type: "text" },
    ],
    list: {
      key: "steps",
      label: "Steps",
      defaultItem: () => ({ id: `st-${Date.now()}`, n: "0", icon: "CheckCircle2", title: "New step", desc: "", enabled: true }),
      itemLabel: (s) => `${s.n} · ${s.title}`,
      hasItemToggle: true,
      fields: [
        { key: "n", label: "Step number (e.g. 01)", type: "text" },
        { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
        { key: "title", label: "Title", type: "text" },
        { key: "desc", label: "Description", type: "textarea" },
      ],
    },
  },
  testimonials: {
    label: "Testimonials",
    description: "Customer reviews carousel.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Title", type: "text" },
    ],
    list: {
      key: "items",
      label: "Reviews",
      defaultItem: () => ({ id: `tm-${Date.now()}`, text: "", name: "Anonymous", role: "City, MA", enabled: true }),
      itemLabel: (r) => r.name,
      hasItemToggle: true,
      fields: [
        { key: "text", label: "Review text", type: "textarea" },
        { key: "name", label: "Reviewer name", type: "text" },
        { key: "role", label: "Reviewer role/location", type: "text" },
      ],
    },
  },
  areas: {
    label: "Service Areas",
    description: "Cities and towns you serve.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
    ],
    list: {
      key: "items",
      label: "Cities",
      defaultItem: () => ({ id: `ar-${Date.now()}`, name: "New City", enabled: true }),
      itemLabel: (a) => a.name,
      hasItemToggle: true,
      fields: [
        { key: "name", label: "Name", type: "text" },
      ],
    },
  },
  cta: {
    label: "CTA Banner",
    description: "Call-to-action banner between sections.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "headline_1", label: "Headline", type: "text" },
      { key: "headline_accent", label: "Headline accent (gold)", type: "text" },
      { key: "subheadline", label: "Subheadline", type: "textarea" },
      { key: "primary_cta", label: "Primary button label", type: "text" },
      { key: "secondary_cta", label: "Secondary button label", type: "text" },
    ],
  },
  contact: {
    label: "Contact / Quote Form",
    description: "Contact section with the request-a-quote form.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title_1", label: "Title (white)", type: "text" },
      { key: "title_accent", label: "Title accent (gold)", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "service_area_label", label: "Service area label", type: "text" },
      { key: "submit_label", label: "Submit button label", type: "text" },
      { key: "services_options", label: "Service options (comma separated)", type: "csv" },
    ],
  },
  footer: {
    label: "Footer",
    description: "Footer content and copyright.",
    fields: [
      { key: "tagline", label: "Tagline", type: "textarea" },
      { key: "services_list", label: "Services list (comma separated)", type: "csv" },
      { key: "copyright", label: "Copyright text", type: "text" },
    ],
  },
};
